import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { t } from '@lingui/core/macro';
import { CircularProgress, colorSystem, TertiaryIconButton } from '@luminovo/design-system';
import { TableCell } from '@mui/material';
import { GripVerticalIcon } from 'lucide-react';
import type { JSX } from 'react';

import { DataTableLegacyRow, DataTableLegacyRowProps } from '../DataTableLegacy';

export function SortableDataTableLegacyRow<T>(
    dataTableRowProps: DataTableLegacyRowProps<T> & {
        rowId: string;
        // Determines the state of the row
        // default - if it is draggable
        // updating - if it was dragged and is currently updated
        // disabled - if another item was dragged and is currently updating
        state: 'default' | 'updating' | 'disabled';
    },
): JSX.Element {
    const { rowId, state, tableState } = dataTableRowProps;
    const { attributes, listeners, transform, transition, isDragging, setNodeRef, setActivatorNodeRef } = useSortable({
        id: rowId,
    });
    const { queries, selectedFilterIds } = tableState;
    const isFilterActive = selectedFilterIds.length > 0 || Object.keys(queries ?? {}).length > 0;
    const columns = [
        {
            id: 'drag-handle',
            label: '',
            width: '10px',
            render: () => {
                return (
                    <TableCell size="small" align="center">
                        <TertiaryIconButton
                            aria-label={t`Drag to reorder`}
                            size="medium"
                            ref={setActivatorNodeRef}
                            disabled={isFilterActive || state === 'disabled'}
                            style={{ cursor: 'grab', color: 'inherit', width: '10px' }}
                            {...listeners}
                            {...attributes}
                        >
                            {state === 'updating' ? (
                                <CircularProgress size="small" />
                            ) : (
                                <GripVerticalIcon color={state === 'disabled' ? colorSystem.neutral[1] : undefined} />
                            )}
                        </TertiaryIconButton>
                    </TableCell>
                );
            },
        },
        ...dataTableRowProps.columns,
    ];

    return (
        <DataTableLegacyRow
            {...dataTableRowProps}
            columns={columns}
            style={{
                ...dataTableRowProps.style,
                transition,
                opacity: isDragging ? 0.5 : undefined,
                transform: CSS.Transform.toString(transform),
            }}
            ref={setNodeRef}
        />
    );
}
